/* eslint-disable react/no-array-index-key */
import React, { useCallback, useState } from "react";
import {
	Slider,
	MenuItem,
	Select,
	TextField,
	Typography
} from "@material-ui/core";
import Grid from "@material-ui/core/Grid";
import Box from "@material-ui/core/Box";
import { Icon, SecondaryButton } from "@prestosports/commons-ui";
import Add32 from "@carbon/icons-react/lib/add/32";
import { YardConverter } from "@prestosports/prestostats-core/Common";
import fieldMarkersEditorStyles from "../../styles/FieldMarkersEditor.styles";

// Same palette as the BallPosition field overlay so config preview matches
// the in-game field rendering.
const AREA_COLORS = {
	redZone: "rgba(211, 47, 47, 0.22)",
	noRunZone: "rgba(255, 167, 38, 0.30)",
	endZone: "rgba(120, 120, 120, 0.22)",
	custom: "rgba(33, 150, 243, 0.22)"
};

// Solid counterparts for marker bodies and legend dots (the translucent band
// colors wash out on the pentagon).
const AREA_MARKER_COLORS = {
	redZone: "#d32f2f",
	noRunZone: "#f57c00",
	endZone: "#616161",
	custom: "#1976d2"
};

// Display names for area types not present in the configurable picker list
// (e.g. noRunZone is hidden from the picker for now but preset/persisted areas
// of that type must still show a readable label).
const AREA_TYPE_FALLBACK_LABELS = {
	noRunZone: "No Running Zone"
};

const buildMarks = (fieldLength) => {
	const half = fieldLength / 2;
	const marks = [];
	for (let i = 0; i <= fieldLength; i += 10) {
		marks.push({
			value: i,
			label: i < half ? i : fieldLength - i
		});
	}
	return marks;
};

const sortedUnique = (values) => {
	return [...new Set(values)].sort((a, b) => a - b);
};

const nextSpot = (spots, fieldLength) => {
	let candidate = Math.round(fieldLength / 2);
	while (spots.includes(candidate) && candidate < fieldLength) {
		candidate += 1;
	}
	return candidate;
};

// Inline copy of assets/images/ball-position-border@1x.svg with the body fill
// parametrized. The asset hardcodes fill="#FFFFFF", and a CSS background-image
// cannot be recolored, so painting the marker body requires inline SVG. The
// navy border and arrow are kept so the shape matches the in-game marker.
// Styles are static (no makeStyles-with-props): thumbs re-render on every
// mousemove during a drag, and dynamic JSS updates there freeze the UI.
// Exported for read-only marker rendering elsewhere (FieldPositionPicker).
export const ColoredMarker = ({ title, subtitle, fill, textColor }) => {
	const classes = fieldMarkersEditorStyles();

	return (
		<span
			className={classes.coloredMarker}
			style={textColor ? { color: textColor } : undefined}
		>
			<svg
				className={classes.coloredMarkerIcon}
				viewBox="0 0 29 36"
				xmlns="http://www.w3.org/2000/svg"
				aria-hidden="true"
				focusable="false"
			>
				<g transform="translate(0.379747, 1.000000)" fillRule="evenodd">
					<path
						d="M8.48123912,3.24506603 L27.2839816,3.24506603 C29.4931206,3.24506603 31.2839816,5.03592704 31.2839816,7.24506603 L31.2839816,27.245066 C31.2839816,29.454205 29.4931206,31.245066 27.2839816,31.245066 L8.48123912,31.245066 C7.19988892,31.245066 5.99609596,30.6312027 5.24362111,29.5940721 L-2.86387504,18.4195691 C-3.3721297,17.7190454 -3.3721297,16.7710867 -2.86387504,16.070563 L5.24362111,4.89605995 C5.99609596,3.85892933 7.19988892,3.24506603 8.48123912,3.24506603 Z"
						fill={fill}
						stroke="#202D43"
						transform="translate(14.000000, 17.245066) rotate(-90.000000) translate(-14.000000, -17.245066)"
					/>
					<path
						d="M16.2710964,27.2781636 L16.2710964,32.6440517 C16.2710964,33.1963364 15.8233811,33.6440517 15.2710964,33.6440517 C14.9533779,33.6440517 14.6545651,33.4930712 14.4660785,33.2373022 L12.4889077,30.5543581 C12.2289316,30.2015806 12.2289316,29.7206346 12.4889077,29.3678571 L14.4660785,26.6849131 C14.7937217,26.240314 15.4197478,26.1455025 15.8643469,26.4731457 C16.120116,26.6616323 16.2710964,26.9604451 16.2710964,27.2781636 Z"
						fill="#202D43"
						transform="translate(14.271096, 29.961084) rotate(-90.000000) translate(-14.271096, -29.961084)"
					/>
				</g>
			</svg>
			<Grid
				direction="column"
				alignItems="center"
				justifyContent="center"
				container
				className={classes.coloredMarkerGrid}
			>
				<Grid item>{title?.toUpperCase()}</Grid>
				<Grid item>{subtitle}</Grid>
			</Grid>
		</span>
	);
};

// Pentagon thumb like the "Yard to Gain" picker, plus a small rounded × badge
// to delete the marker in place. The badge stops mousedown/touchstart
// propagation so pressing it never starts a slider drag. With markerColor the
// body is painted in the area's color (white text); without it the body stays
// white and the text inherits the same theme color the in-game marker uses.
const MarkerThumb = (props) => {
	const { fieldLength, onDelete, showDelete, markerColor, ...thumbProps } =
		props;
	const classes = fieldMarkersEditorStyles();

	const { "aria-valuenow": value, "data-index": index } = thumbProps;
	const yardConverter = new YardConverter(fieldLength);
	const { teamSide, yard } = yardConverter.convertFromFieldPosition(value);

	const color =
		typeof markerColor === "function" ? markerColor(Number(index)) : markerColor;

	return (
		// eslint-disable-next-line react/jsx-props-no-spreading
		<span {...thumbProps}>
			<Box style={{ position: "absolute", bottom: 14 }}>
				<Box className={classes.markerWrapper}>
					<ColoredMarker
						title={teamSide}
						subtitle={yard}
						fill={color || "#FFFFFF"}
						textColor={color ? "#FFFFFF" : undefined}
					/>
					{showDelete && (
						<Box
							component="button"
							type="button"
							aria-label="Remove marker"
							className={classes.deleteBadge}
							onMouseDown={(e) => e.stopPropagation()}
							onTouchStart={(e) => e.stopPropagation()}
							onClick={(e) => {
								e.stopPropagation();
								onDelete(Number(index));
							}}
						>
							×
						</Box>
					)}
				</Box>
			</Box>
		</span>
	);
};

// Yard-to-gain flag style: first downs come from a fixed chain distance
// (general.firstDownYards, consumed by core first-down.js), measured from the
// ball spot and reset each first down — there are NO fixed field lines, so this
// mode has no ruler/markers, just the distance input.
const YardToGainEditor = ({ yardsToGain, fieldLength, disabled, onChange }) => {
	// Plain distance input that fills its settings-grid cell like the sibling
	// fields (the parent cell is already sized; no inner Grid).
	return (
		<TextField
			id="first-down-yards"
			label="First down every (yds)"
			type="number"
			fullWidth
			value={yardsToGain}
			disabled={disabled}
			InputProps={{ inputProps: { min: 1, max: fieldLength } }}
			onChange={(e) => onChange(Number(e.target.value))}
		/>
	);
};

const PointMarkers = ({ value, fieldLength, disabled, marks, onChange }) => {
	const classes = fieldMarkersEditorStyles();
	const spots = sortedUnique(value);
	const spotsKey = spots.join(",");

	// Controlled slider with a local draft during the drag. An uncontrolled
	// slider remounted via key loses in-flight drags and made every mousemove
	// remount the thumbs (UI freeze); the draft keeps drag state local and only
	// commits to the form on release.
	const [draft, setDraft] = useState(null);
	const shown = draft || spots;

	const addSpot = () => {
		onChange(sortedUnique([...spots, nextSpot(spots, fieldLength)]));
	};

	const removeSpot = useCallback(
		(index) => {
			const current = spotsKey === "" ? [] : spotsKey.split(",").map(Number);
			onChange(current.filter((_, i) => i !== index));
		},
		[spotsKey, onChange]
	);

	// Stable thumb component: a new identity per render would remount all
	// thumbs on every drag step.
	const thumb = useCallback(
		(props) => (
			// eslint-disable-next-line react/jsx-props-no-spreading
			<MarkerThumb
				{...props}
				fieldLength={fieldLength}
				showDelete={!disabled}
				onDelete={removeSpot}
			/>
		),
		[fieldLength, disabled, removeSpot]
	);

	return (
		<Box component="fieldset" className={classes.editor}>
			<Typography
				component="legend"
				variant="caption"
				color="textSecondary"
				className={classes.sectionTitle}
			>
				Zone To Gain
			</Typography>
			{/* Ruler always renders so the field stays visible with zero spots;
			    with no thumbs it is made inert (empty value + no pointer). */}
			<Box
				className={classes.rulerSpacing}
				style={shown.length ? undefined : { pointerEvents: "none" }}
			>
				<Slider
					className={classes.ruler}
					min={0}
					max={fieldLength}
					step={1}
					track={false}
					disabled={disabled}
					value={shown}
					marks={marks}
					valueLabelDisplay="off"
					ThumbComponent={thumb}
					onChange={(_, next) => setDraft(Array.isArray(next) ? next : [next])}
					onChangeCommitted={(_, next) => {
						setDraft(null);
						onChange(sortedUnique(Array.isArray(next) ? next : [next]));
					}}
				/>
			</Box>
			<SecondaryButton
				id="button-add-first-down-spot"
				disabled={disabled}
				startIcon={
					<Icon>
						<Add32 />
					</Icon>
				}
				onClick={addSpot}
				label="Add spot"
			/>
		</Box>
	);
};

const RegionMarkers = ({
	value,
	fieldLength,
	disabled,
	marks,
	areaTypes,
	onChange
}) => {
	const classes = fieldMarkersEditorStyles();
	const areas = [...(Array.isArray(value) ? value : [])].sort(
		(a, b) => a.start - b.start
	);
	const defaultType = areaTypes[0]?.code;
	const typesKey = areas.map((a) => a.type).join(",");

	// All areas live on ONE ruler. Non-overlap is structural: the slider keeps
	// its values sorted, and areas are read back as consecutive [start, end]
	// pairs of that sorted list, so bands can touch but never overlap.
	const boundaries = areas.flatMap((a) => [a.start, a.end]);

	// Controlled slider + local drag draft (see PointMarkers).
	const [draft, setDraft] = useState(null);
	const shown = draft || boundaries;

	// First gap that fits a new area (checked before 0, between areas, after
	// the last area). Null when the field is fully covered.
	const findGap = () => {
		let gap = null;
		let cursor = 0;
		areas.forEach((a) => {
			if (gap === null && a.start > cursor) {
				gap = { start: cursor, end: a.start };
			}
			cursor = Math.max(cursor, a.end);
		});
		if (gap === null && cursor < fieldLength) {
			gap = { start: cursor, end: fieldLength };
		}
		return gap;
	};

	const addArea = () => {
		const gap = findGap();
		if (gap) {
			onChange(
				[
					...areas,
					{
						start: gap.start,
						end: Math.min(gap.start + 10, gap.end),
						type: defaultType
					}
				].sort((a, b) => a.start - b.start)
			);
		}
	};

	const setAreaType = (index, type) => {
		onChange(areas.map((a, i) => (i === index ? { ...a, type } : a)));
	};

	// Map a sorted boundary list back to areas, keeping each type with its
	// pair position. Pairs dragged to zero width are dropped.
	const toAreas = (sorted) => {
		return areas
			.map((a, i) => ({
				start: sorted[i * 2],
				end: sorted[i * 2 + 1],
				type: a.type
			}))
			.filter((a) => a.start < a.end);
	};

	// × on either handle of an area removes the whole area.
	const removeAreaByThumb = useCallback(
		(thumbIndex) => {
			const areaIndex = Math.floor(thumbIndex / 2);
			onChange(
				[...(Array.isArray(value) ? value : [])]
					.sort((a, b) => a.start - b.start)
					.filter((_, i) => i !== areaIndex)
			);
		},
		[value, onChange]
	);

	const markerColor = useCallback(
		(thumbIndex) => {
			const types = typesKey.split(",");
			return (
				AREA_MARKER_COLORS[types[Math.floor(thumbIndex / 2)]] ||
				AREA_MARKER_COLORS.custom
			);
		},
		[typesKey]
	);

	// Stable thumb component: a new identity per render would remount all
	// thumbs on every drag step.
	const thumb = useCallback(
		(props) => (
			// eslint-disable-next-line react/jsx-props-no-spreading
			<MarkerThumb
				{...props}
				fieldLength={fieldLength}
				showDelete={!disabled}
				onDelete={removeAreaByThumb}
				markerColor={markerColor}
			/>
		),
		[fieldLength, disabled, removeAreaByThumb, markerColor]
	);

	// Bands follow the drag draft for live feedback.
	const shownSorted = [...shown].sort((a, b) => a - b);
	const bands = areas.map((area, index) => {
		const start = shownSorted[index * 2];
		const end = shownSorted[index * 2 + 1];
		return (
			<Box
				key={`band-${index}`}
				className={classes.areaBand}
				style={{
					left: `${(start / fieldLength) * 100}%`,
					width: `${((end - start) / fieldLength) * 100}%`,
					backgroundColor: AREA_COLORS[area.type] || AREA_COLORS.custom
				}}
			/>
		);
	});

	return (
		<Box component="fieldset" className={classes.editor}>
			<Typography
				component="legend"
				variant="caption"
				color="textSecondary"
				className={classes.sectionTitle}
			>
				Field Areas
			</Typography>
			{/* Ruler always renders so the field stays visible with zero areas
			    (e.g. right after a preset that clears them); with no thumbs it
			    is made inert (empty value + no pointer). */}
			<Box className={classes.rulerSpacing}>
				<Box
					className={classes.rulerWrapper}
					style={areas.length ? undefined : { pointerEvents: "none" }}
				>
					{bands}
					<Slider
						className={classes.ruler}
						min={0}
						max={fieldLength}
						step={1}
						track={false}
						disabled={disabled}
						value={shown}
						marks={marks}
						valueLabelDisplay="off"
						ThumbComponent={thumb}
						onChange={(_, next) => setDraft(Array.isArray(next) ? next : [next])}
						onChangeCommitted={(_, next) => {
							setDraft(null);
							onChange(toAreas([...next].sort((a, b) => a - b)));
						}}
					/>
				</Box>
			</Box>
			<Box className={classes.footerRow}>
				<SecondaryButton
					id="button-add-field-area"
					disabled={disabled || !findGap()}
					startIcon={
						<Icon>
							<Add32 />
						</Icon>
					}
					onClick={addArea}
					label="Add area"
				/>
				<Box className={classes.typeChipList}>
					{areas.map((area, index) => (
						<Box key={`type-${index}`} className={classes.typeChip}>
							<Box
								className={classes.typeDot}
								style={{
									backgroundColor:
										AREA_MARKER_COLORS[area.type] || AREA_MARKER_COLORS.custom
								}}
							/>
							<Select
								disabled={disabled}
								value={area.type}
								onChange={(e) => setAreaType(index, e.target.value)}
							>
								{areaTypes.map((t) => (
									<MenuItem key={t.code} value={t.code}>
										{t.name}
									</MenuItem>
								))}
								{!areaTypes.some((t) => t.code === area.type) && (
									<MenuItem key={area.type} value={area.type} disabled>
										{AREA_TYPE_FALLBACK_LABELS[area.type] || area.type}
									</MenuItem>
								)}
							</Select>
							<Box component="span" className={classes.typeRange}>
								{area.start}–{area.end} yd
							</Box>
						</Box>
					))}
				</Box>
			</Box>
		</Box>
	);
};

const FieldMarkersEditor = ({
	value = [],
	fieldLength,
	markerKind = "point",
	areaTypes = [],
	disabled = false,
	onChange = () => {},
	firstDownStyle,
	yardsToGain,
	onYardsToGainChange = () => {}
}) => {
	const marks = buildMarks(fieldLength);

	// FirstDownStyles: YardToGain = -1, ZoneToGain = 1. The spots ruler only
	// applies to zone-to-gain; with yard-to-gain selected the point editor
	// switches to the chain-distance input.
	const isYardToGain =
		markerKind !== "region" &&
		typeof firstDownStyle === "number" &&
		firstDownStyle <= 0;

	let editor;
	if (markerKind === "region") {
		editor = (
			<RegionMarkers
				value={value}
				fieldLength={fieldLength}
				disabled={disabled}
				marks={marks}
				areaTypes={areaTypes}
				onChange={onChange}
			/>
		);
	} else if (isYardToGain) {
		editor = (
			<YardToGainEditor
				yardsToGain={yardsToGain}
				fieldLength={fieldLength}
				disabled={disabled}
				onChange={onYardsToGainChange}
			/>
		);
	} else {
		editor = (
			<PointMarkers
				value={value}
				fieldLength={fieldLength}
				disabled={disabled}
				marks={marks}
				onChange={onChange}
			/>
		);
	}

	return editor;
};

export default FieldMarkersEditor;
